import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Gem } from "lucide-react";

const items = [
  { t: "MCap / TVL (or FDV / TVL)", d: "How much economic activity each dollar of market cap is supporting. Lower is better." },
  { t: "MCap / Annualized Revenue", d: "Crypto-native P/E ratio. We target tokens trading below 15–20x annualized protocol revenue." },
  { t: "On-chain usage growth", d: "Active addresses, transaction volume, fee growth over 30/90 days." },
  { t: "NVT Ratio", d: "Network value vs transaction throughput — a Bitcoin-style fundamental valuation lens." },
  { t: "Developer activity", d: "GitHub commits, contributors, repo health. Real building vs marketing fluff." },
  { t: "Token unlock pressure", d: "Upcoming supply unlocks that could dilute price. Heavy unlocks = penalty." },
  { t: "Revenue growth", d: "QoQ direction matters. Flat or declining revenue cools the score even if cheap." },
  { t: "Swyftx liquidity", d: "All picks must be tradeable on Swyftx with reasonable AUD depth." },
];

const Methodology = () => (
  <div className="min-h-screen bg-background">
    <div className="container mx-auto px-4 py-10 max-w-4xl">
      <Link to="/undervalued-gems" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to gems
      </Link>

      <div className="inline-flex items-center gap-2 mb-2">
        <Gem className="w-5 h-5 text-emerald-400" />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400">Methodology</span>
      </div>
      <h1 className="text-4xl md:text-5xl font-bold mb-4">How We Pick Undervalued Gems</h1>
      <p className="text-muted-foreground text-lg mb-8">
        Undervalued Gems is fundamentally different from the TOSS score. TOSS rewards momentum and
        technical setups. Undervalued Gems rewards <em>real value</em> — tokens whose usage,
        revenue, and on-chain adoption justify a higher market cap than they currently command.
      </p>

      <Card className="p-6 mb-6">
        <h2 className="text-xl font-bold mb-3">The 8 Factors We Score</h2>
        <ol className="space-y-3">
          {items.map((it, i) => (
            <li key={i} className="flex gap-3">
              <span className="font-mono text-emerald-400 font-bold w-6 shrink-0">{i + 1}.</span>
              <div>
                <div className="font-semibold">{it.t}</div>
                <div className="text-sm text-muted-foreground">{it.d}</div>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="p-6 mb-6">
        <h2 className="text-xl font-bold mb-3">How the eight are ranked</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Candidates are filtered on the factors above, then ranked by an AI pass that weighs value
          (market cap versus usage and revenue) against liquidity and unlock risk. Because that final
          ranking is a judgement call rather than a fixed formula, we don't publish it as a numeric
          score — the cards show the underlying figures instead (market cap, 24h volume, 7-day change
          and the rationale), so you can check the case yourself rather than trust a number.
        </p>
      </Card>


      <Card className="p-6 mb-6">
        <h2 className="text-xl font-bold mb-3">Universe Filter</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Only tokens listed on Swyftx are eligible. We prefer mid- and low-cap tokens with real
          utility, and exclude meme coins, tokens with no revenue stream, and anything with severe
          unlock cliffs in the next 90 days.
        </p>
      </Card>

      <Card className="p-6 border-amber-500/30 bg-amber-500/5">
        <h2 className="text-xl font-bold mb-2">Risk Disclaimer</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Crypto is volatile and undervalued ≠ guaranteed to go up. Fundamentals can stay
          mispriced for long periods. This is research, not financial advice. Always do your own
          due diligence and never invest more than you can afford to lose.
        </p>
      </Card>
    </div>
  </div>
);

export default Methodology;
